import React, { useContext } from 'react';
import {
	ApButtonTab,
	ApLoaderDefault,
	ApLoaderText,
	ApSecondaryNav,
} from '@alixpartners/ui-components';

import { Content, Wrapper } from './TaxonomyMapper.styles';
import { PermissionsContext } from 'contexts/PermissionsContext';
import { QueryStringContext } from 'contexts/QueryStringContext';
import { SettingsOptionsContext } from 'contexts/SettingsOptionsContext';
import { UploadStateContext } from 'contexts/UploadStateContext';
import { ApiDataContext } from 'contexts/ApiDataContext';
import { CHART_COLORS } from 'styles/colors';
import {
	getApplyDataFromTree,
	getCompanyTreeData,
	later,
	syncValuesFromTreeData,
	markMappedItems,
	applyAnonomizationTreeData,
} from './utils';
import { taxonomyMapperDataConverter } from 'utils/taxonomyMapperDataConverter';
import { SecondaryNav, SAVE_MESSAGE_TYPES } from './SecondaryNav';
import { TaxonomyMapper } from './TaxonomyMapper';
import { WarningDialog } from './WarningDialog';
import { RevertDialog } from './RevertDialog';
import {
	fetchWheelChartData,
	fetchTaxonomyMapperData,
	saveTaxonomyMapperData,
	applyTaxonomyDirect,
	getAppState,
	saveAppliedTaxonomyMapperData,
	fetchAppliedTaxonomyMapperData,
	getCompanyAnonymizedMapping,
	isDemoMode,
} from 'utils/apiRequests';
import { getCurrencySymbolFromOptions } from 'Details/utils';
import { UPLOAD_STATUS } from 'Data/constants';
import { getCompanySpendItemsFromTree, hasTreeChanges } from './utils';
import { BASELINE_NAV_CONFIG } from 'SecondaryNav';
import { NavLink } from 'react-router-dom';

export const TAXONOMY_TREE_DATA_PARAMS = {
	dimensions: [
		'company',
		'clientCategory',
		'clientCategory',
		'clientCategory',
		'clientCategory',
	],
	harmonized: true,
	isPmi: true,
	filters: 'spendType',
	filterValues: 0,
	disableTailSpendSegments: true,
};

export class TaxonomyMapperContainer extends React.Component {
	state = {
		undoHistory: [],
		projectTreeData: [],
		appliedTreeData: [],
		companiesOptions: [],
		companyA: null,
		companyB: null,
		companyATreeData: null,
		companyBTreeData: null,
		treeData: null,
		companyASearch: '',
		companyBSearch: '',
		projectSearch: '',
		isApplyWarningOpen: false,
		isRevertDialogOpen: false,
		skipUndoChange: false,
		isApplying: false,
		isLoading: true,
	};

	componentDidMount() {
		this.fetchData();

		document.addEventListener('click', this.clearSearchValues, true);
	}

	componentWillUnmount() {
		document.removeEventListener('click', this.clearSearchValues);
		this.unmounted = true;
	}

	clearSearchValues = () => {
		const { companyASearch, companyBSearch, projectSearch } = this.state;

		if (companyBSearch || companyASearch || projectSearch) {
			this.setState({
				companyASearch: '',
				companyBSearch: '',
				projectSearch: '',
			});
		}
	};

	async fetchData() {
		await this.fetchTreeData();
		await this.fetchSavedProjectTreeData();

		this.getCompanies();
	}

	getCompanies() {
		const { treeData } = this.state;
		const companiesList = treeData.map(({ name }) => name).sort();

		if (!companiesList || companiesList.length === 0) {
			this.setState({
				isLoading: false,
				companiesOptions: [],
				companyA: {},
				companyB: {},
			});

			return;
		}

		const companiesOptions = companiesList.map((company, index) => ({
			label: company,
			value: company,
			color: CHART_COLORS[index],
		}));

		this.setState({
			isLoading: false,
			companiesOptions,
			companyA: companiesOptions[0],
			companyB:
				companiesOptions.length > 1 ? companiesOptions[1] : companiesOptions[0],
		});
	}

	async fetchTreeData() {
		const { currency } = this.props;
		const apiRequestParams = { ...TAXONOMY_TREE_DATA_PARAMS };

		if (currency) {
			apiRequestParams.currency = currency;
			apiRequestParams.currencyType = 'fixed';
		}

		const data = await fetchWheelChartData(apiRequestParams);
		let treeData = taxonomyMapperDataConverter.convert(data.data);

		this.setState({ treeData });
	}

	async fetchSavedProjectTreeData() {
		const { treeData } = this.state;
		let projectTreeData = await fetchTaxonomyMapperData();
		let appliedTreeData = await fetchAppliedTaxonomyMapperData();

		let companyAnonymizedMapping = await getCompanyAnonymizedMapping();

		projectTreeData = applyAnonomizationTreeData(
			projectTreeData,
			companyAnonymizedMapping,
			isDemoMode
		);

		appliedTreeData = applyAnonomizationTreeData(
			appliedTreeData,
			companyAnonymizedMapping,
			isDemoMode
		);

		projectTreeData = syncValuesFromTreeData(projectTreeData, treeData);
		appliedTreeData = syncValuesFromTreeData(appliedTreeData, treeData);

		this.setState(
			{
				projectTreeData,
				appliedTreeData,
				skipUndoChange: true,
			},
			() => {
				this.setState({ skipUndoChange: false });
			}
		);
	}

	componentDidUpdate(prevProps, prevState) {
		const { showMappedItems } = this.props;
		let {
			companyA,
			companyB,
			treeData,
			projectTreeData,
			companyATreeData: prevCompanyATreeData,
			companyBTreeData: prevCompanyBTreeData,
			undoHistory,
			skipUndoChange,
		} = this.state;

		if (
			prevProps.showMappedItems !== showMappedItems ||
			prevState.companyA !== companyA
		) {
			const companyATreeData = getCompanyTreeData({
				companyOption: companyA,
				treeData,
				projectTreeData,
				showMappedItems,
				prevTreeData: prevCompanyATreeData,
			});

			this.setState({ companyATreeData });
		}

		if (
			prevProps.showMappedItems !== showMappedItems ||
			prevState.companyB !== companyB
		) {
			const companyBTreeData = getCompanyTreeData({
				companyOption: companyB,
				treeData,
				projectTreeData,
				showMappedItems,
				prevTreeData: prevCompanyBTreeData,
			});

			this.setState({ companyBTreeData });
		}

		if (
			hasTreeChanges(projectTreeData, prevState.projectTreeData) &&
			!skipUndoChange
		) {
			this.setState({
				undoHistory: [...undoHistory, prevState.projectTreeData],
			});
			this.handleSave();
		}
	}

	handleChange = stateUpdates => {
		this.setState(stateUpdates);
	};

	handleUndo = () => {
		const { undoHistory } = this.state;
		const prevTreeData = undoHistory.slice(-1)[0];
		const remainingUndos = undoHistory.slice(0, -1);
		let stateUpdates = { projectTreeData: prevTreeData };

		stateUpdates = this.getCompanyTreeDataStateUpdates(stateUpdates);
		this.setState(
			{
				...stateUpdates,
				skipUndoChange: true,
				undoHistory: remainingUndos,
			},
			() => {
				this.setState({ skipUndoChange: false });
				this.handleSave();
			}
		);
	};

	handleRevert = () => {
		const { appliedTreeData } = this.state;
		let stateUpdates = { projectTreeData: appliedTreeData };

		stateUpdates = this.getCompanyTreeDataStateUpdates(stateUpdates);

		this.setState(stateUpdates);
		this.hideRevertDialog();
	};

	getCompanyTreeDataStateUpdates = stateUpdates => {
		stateUpdates = { ...stateUpdates };

		const { projectTreeData } = stateUpdates;
		const {
			companyATreeData,
			companyBTreeData,
			treeData,
			companyA,
			companyB,
		} = this.state;

		const { showMappedItems } = this.props;

		if (showMappedItems) {
			stateUpdates.companyATreeData = markMappedItems(
				companyATreeData,
				projectTreeData
			);

			stateUpdates.companyBTreeData = markMappedItems(
				companyBTreeData,
				projectTreeData
			);
		} else {
			// complete reset needed for the case when removing some of the projectTree items
			const companyATreeData = getCompanyTreeData({
				companyOption: companyA,
				treeData,
				projectTreeData,
				showMappedItems,
			});
			const companyBTreeData = getCompanyTreeData({
				companyOption: companyB,
				treeData,
				projectTreeData,
				showMappedItems,
			});

			stateUpdates.companyATreeData = companyATreeData;
			stateUpdates.companyBTreeData = companyBTreeData;
		}

		return stateUpdates;
	};

	handleApply = async () => {
		const { projectTreeData } = this.state;
		const { stateRefetch, clearApiCache } = this.props;

		const applyData = getApplyDataFromTree(projectTreeData);
		// Refetch state provider on App level to check for blocking changes
		stateRefetch();

		try {
			const status = await getAppState();

			if (status.state !== UPLOAD_STATUS.CLASSIFYING) {
				this.setState({ isApplying: true });
				await applyTaxonomyDirect(applyData);
				await saveAppliedTaxonomyMapperData(projectTreeData);
				stateRefetch();

				clearApiCache();
			} else {
				this.showApplyWarning();
			}
		} catch (e) {
			// TODO: show apply failed message
			this.setState({ isApplying: false });
		}
	};

	handleSave = async () => {
		const { projectTreeData } = this.state;

		try {
			this.setState({ saveMessageType: SAVE_MESSAGE_TYPES.inprogress });
			await Promise.all([later(1000), saveTaxonomyMapperData(projectTreeData)]);
			this.setState({ saveMessageType: SAVE_MESSAGE_TYPES.success });
		} catch (e) {
			this.setState({ saveMessageType: SAVE_MESSAGE_TYPES.fail });
		}
	};

	handleShowMappedItemsChange = () => {
		const { setQueryStringStateAndPush, showMappedItems } = this.props;

		setQueryStringStateAndPush({
			showMappedItems: showMappedItems ? undefined : true,
		});
	};

	getCompaniesOptions = () => {
		const { projectTreeData, companiesOptions, treeData } = this.state;

		return companiesOptions.map(item => {
			const companyObj = treeData.find(({ name }) => name === item.value);
			const totalSpend = companyObj ? companyObj.value : 0;
			const mappedSpend = getCompanySpendItemsFromTree(
				projectTreeData,
				item.value
			).reduce((acc, item) => {
				return acc + item.value;
			}, 0);

			return {
				...item,
				spend: totalSpend,
				mappedSpend,
				percentage: Math.round((mappedSpend / totalSpend) * 100) || 0,
			};
		});
	};

	handleBackButtonClick = () => {
		const { history } = this.props;

		const prevPageUrl = this.getPrevPageUrl();

		history.push(prevPageUrl);
	};

	showApplyWarning = () => {
		this.setState({ isApplyWarningOpen: true });
	};

	hideApplyWarning = () => {
		this.setState({ isApplyWarningOpen: false });
	};

	showRevertDialog = () => {
		this.setState({ isRevertDialogOpen: true });
	};

	hideRevertDialog = () => {
		this.setState({ isRevertDialogOpen: false });
	};

	getPrevPageUrl() {
		const {
			location: { search },
		} = this.props;

		const params = new URLSearchParams(search);

		return params.get('prevPageUrl');
	}

	render() {
		const {
			permissions,
			showMappedItems,
			currencySymbol,
			history,
			location,
		} = this.props;
		const historyProps = { history, location };

		const {
			undoHistory,
			companyATreeData,
			companyBTreeData,
			projectTreeData,
			companyA,
			companyB,
			companyASearch,
			companyBSearch,
			projectSearch,
			treeData,
			saveMessageType,
			isApplyWarningOpen,
			isRevertDialogOpen,
			appliedTreeData,
			isApplying,
			isLoading,
		} = this.state;
		const hasApplyChanges = hasTreeChanges(projectTreeData, appliedTreeData);
		const isApplyDisabled =
			isApplying ||
			getApplyDataFromTree(projectTreeData).length === 0 ||
			!hasApplyChanges;
		const isRevertDisabled =
			!hasApplyChanges || (!appliedTreeData || appliedTreeData.length === 0);

		if (isLoading) {
			return (
				<Wrapper>
					<ApLoaderDefault loaderText={() => <ApLoaderText />} />
				</Wrapper>
			);
		}

		return (
			<Wrapper>
				<ApSecondaryNav
					left={BASELINE_NAV_CONFIG.map(({ label, path, disabled }) => (
						<NavLink to={path} key={path} disabled={disabled}>
							<ApButtonTab
								isSelected={
									path === '/taxonomymapper' ? true : location.pathname === path
								}
								disabled={disabled}
							>
								{label}
							</ApButtonTab>
						</NavLink>
					))}
				/>
				<SecondaryNav
					permissions={permissions}
					showMappedItems={showMappedItems}
					onApply={this.handleApply}
					onUndo={this.handleUndo}
					onShowMappedItemsChange={this.handleShowMappedItemsChange}
					isUndoDisabled={undoHistory.length === 0}
					isApplyDisabled={isApplyDisabled}
					isRevertDisabled={isRevertDisabled}
					saveMessageType={saveMessageType}
					onRevert={this.showRevertDialog}
					prevPageUrl={this.getPrevPageUrl()}
					onBackButtonClick={this.handleBackButtonClick}
					isApplying={isApplying}
				/>
				{isApplyWarningOpen && (
					<WarningDialog onClose={this.hideApplyWarning} />
				)}
				{isRevertDialogOpen && (
					<RevertDialog
						onClose={this.hideRevertDialog}
						onSubmit={this.handleRevert}
					/>
				)}
				<Content>
					<TaxonomyMapper
						currencySymbol={currencySymbol}
						companyATreeData={companyATreeData}
						companyBTreeData={companyBTreeData}
						treeData={treeData}
						projectTreeData={projectTreeData}
						companiesOptions={this.getCompaniesOptions()}
						companyA={companyA}
						companyB={companyB}
						companyASearch={companyASearch}
						companyBSearch={companyBSearch}
						projectSearch={projectSearch}
						onChange={this.handleChange}
						onShowMappedItemsChange={this.handleShowMappedItemsChange}
						showMappedItems={showMappedItems}
						getCompanyTreeDataStateUpdates={this.getCompanyTreeDataStateUpdates}
						{...historyProps}
					/>
				</Content>
			</Wrapper>
		);
	}
}

export default React.memo(props => {
	const { currency, showMappedItems, setQueryStringStateAndPush } = useContext(
		QueryStringContext
	);
	const { permissions } = useContext(PermissionsContext);
	const {
		options: { currency: currencyList },
	} = useContext(SettingsOptionsContext);
	const { refetch: stateRefetch } = useContext(UploadStateContext);
	const currencySymbol = getCurrencySymbolFromOptions(currencyList, currency);
	const { clearApiCache } = useContext(ApiDataContext);

	return (
		<TaxonomyMapperContainer
			{...props}
			permissions={permissions || []}
			showMappedItems={!!showMappedItems}
			setQueryStringStateAndPush={setQueryStringStateAndPush}
			currencySymbol={currencySymbol}
			currency={currency}
			stateRefetch={stateRefetch}
			clearApiCache={clearApiCache}
		/>
	);
});
